import { useEffect, useState } from "react";
import { CreditCard, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const FEATURE_KEYS = [
  { key: "hub", label: "Public hub & enquiries" },
  { key: "orders", label: "Customer ordering (pickup & on-site)" },
  { key: "toolkit", label: "F&B toolkit (costing, GP, suppliers)" },
  { key: "custom_domain", label: "Custom domain" },
];

const EMPTY = { name: "", description: "", price_monthly: "", max_units: 0, features: { hub: true, orders: false, toolkit: false, custom_domain: false }, active: true };

export default function AdminPlans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/platform/plans").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || "", price_monthly: p.price_monthly, max_units: p.max_units || 0,
      features: { hub: true, orders: false, toolkit: false, custom_domain: false, ...(p.features || {}) }, active: p.active !== false });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price_monthly: Number(form.price_monthly), max_units: Number(form.max_units) };
    try {
      if (editing) await api.patch(`/platform/plans/${editing.id}`, payload);
      else await api.post("/platform/plans", payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try {
      await api.delete(`/platform/plans/${p.id}`);
      toast.success("Plan deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-plans-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Subscription Plans</h1>
          <p className="mt-1 text-sm text-muted-foreground">Feature tiers vendors subscribe to via Stripe monthly billing (test mode).</p>
        </div>
        <Button data-testid="new-plan-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> New Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className={`border border-border bg-white p-6 ${p.active === false ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-muted"><CreditCard className="h-5 w-5 text-ink" /></div>
                <h3 className="font-heading text-lg font-bold">{p.name}</h3>
              </div>
              <span className="font-heading text-2xl font-black text-brand tabular-nums" data-testid={`plan-price-${p.id}`}>{fmt(p.price_monthly)}<span className="text-xs font-medium text-muted-foreground">/mo</span></span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
            <ul className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
              {FEATURE_KEYS.map((f) => (
                <li key={f.key} className={p.features?.[f.key] ? "font-medium" : "text-muted-foreground line-through"}>{f.label}</li>
              ))}
              <li className="text-muted-foreground">{p.max_units ? `Up to ${p.max_units} fleet units` : "Unlimited fleet units"}</li>
            </ul>
            <div className="mt-4 flex gap-2 border-t border-border pt-4">
              <Button data-testid={`edit-plan-${p.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => openEdit(p)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              <Button data-testid={`delete-plan-${p.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No plans yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Plan" : "New Plan"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="plan-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Price (£/month)</Label><Input data-testid="plan-price-input" type="number" step="1" min="0" required value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Input data-testid="plan-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Max fleet units (0 = unlimited)</Label><Input data-testid="plan-max-units-input" type="number" min="0" value={form.max_units} onChange={(e) => setForm({ ...form, max_units: e.target.value })} /></div>
            <div className="space-y-2 border border-border p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Features included</p>
              {FEATURE_KEYS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={`plan-feature-${f.key}`} checked={!!form.features[f.key]} onCheckedChange={(v) => setForm({ ...form, features: { ...form.features, [f.key]: !!v } })} />
                  {f.label}
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between border border-border px-3 py-2">
              <Label className="text-sm">Active (available to vendors)</Label>
              <Switch data-testid="plan-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <Button data-testid="plan-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editing ? "Save Changes" : "Create Plan"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
